import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, Disc3, Film } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { resolveAudio } from "../lib/api";
import { stopAllMediaExcept, onExclusivePlay } from "../lib/mediaBus";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
};

// A single self-contained before/after audio unit (used by EP rows and single cards)
function ABUnit({ before, after, testid, layout = "row", index }) {
  const rawRef = useRef(null);
  const masRef = useRef(null);
  const [mode, setMode] = useState("after");
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const active = () => (mode === "after" ? masRef.current : rawRef.current);

  useEffect(() => {
    const a = active();
    if (!a) return;
    const t = () => setCur(a.currentTime);
    const d = () => setDur(a.duration || 0);
    const e = () => setPlaying(false);
    a.addEventListener("timeupdate", t);
    a.addEventListener("loadedmetadata", d);
    a.addEventListener("ended", e);
    return () => {
      a.removeEventListener("timeupdate", t);
      a.removeEventListener("loadedmetadata", d);
      a.removeEventListener("ended", e);
    };
  }, [mode]);

  const toggle = () => {
    const a = active();
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      stopAllMediaExcept(a);
      a.play(); setPlaying(true);
    }
  };

  useEffect(() => onExclusivePlay((el) => {
    if (el !== rawRef.current && el !== masRef.current) setPlaying(false);
  }), []);

  const switchMode = (m) => {
    if (m === mode) return;
    const from = active();
    const time = from ? from.currentTime : 0;
    const wasPlaying = from ? !from.paused : false;
    if (from) from.pause();
    const to = m === "after" ? masRef.current : rawRef.current;
    setMode(m);
    setCur(time);
    if (!to) return;
    const apply = () => {
      try { to.currentTime = to.duration ? Math.min(time, to.duration) : time; } catch (e) { console.debug("A/B seek deferred:", e); }
      setCur(to.currentTime);
      if (wasPlaying) {
        stopAllMediaExcept(to);
        to.play(); setPlaying(true);
      }
    };
    if (to.readyState >= 1) apply();
    else to.addEventListener("loadedmetadata", apply, { once: true });
  };

  const seek = (v) => { const a = active(); if (a) { a.currentTime = (v[0] / 100) * (dur || 1); setCur(a.currentTime); } };

  const Toggle = (
    <div className="inline-flex p-0.5 rounded-md bg-[#0B0C10] border border-white/10">
      <button onClick={() => switchMode("before")} data-testid={`${testid}-raw`}
        className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-all ${mode === "before" ? "bg-[#272A35] text-white" : "text-[#8b8b93] hover:text-white"}`}>RAW</button>
      <button onClick={() => switchMode("after")} data-testid={`${testid}-mastered`}
        className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-all ${mode === "after" ? "bg-[#C8A97E] text-[#0B0C10]" : "text-[#8b8b93] hover:text-white"}`}>MASTERED</button>
    </div>
  );

  const PlayBtn = (
    <button onClick={toggle} data-testid={`${testid}-play`}
      className={`shrink-0 rounded-full bg-[#C8A97E] text-[#0B0C10] flex items-center justify-center hover:scale-105 transition-transform ${layout === "row" ? "h-9 w-9" : "h-11 w-11"}`}>
      {playing ? <Pause size={layout === "row" ? 15 : 18} fill="currentColor" /> : <Play size={layout === "row" ? 15 : 18} fill="currentColor" className="ml-0.5" />}
    </button>
  );

  return (
    <>
      <audio ref={rawRef} src={resolveAudio(before)} preload="metadata" />
      <audio ref={masRef} src={resolveAudio(after)} preload="metadata" />
      {layout === "row" ? (
        <div className={`flex items-center gap-3 py-3 px-3 rounded-md transition-colors ${playing ? "bg-[#C8A97E]/[0.06]" : "hover:bg-white/[0.03]"}`}>
          {PlayBtn}
          <span className="font-mono text-xs text-[#6b6b73] w-5 text-center shrink-0">{index}</span>
          <div className="flex-1 min-w-0">
            <Slider value={[dur ? (cur / dur) * 100 : 0]} onValueChange={seek} max={100} step={0.1} />
          </div>
          <span className="font-mono text-[11px] text-[#A1A1AA] w-10 text-right shrink-0">{fmt(dur)}</span>
          {Toggle}
        </div>
      ) : (
        <div>
          <div className="mb-3">{Toggle}</div>
          <div className="flex items-center gap-4">
            {PlayBtn}
            <Slider value={[dur ? (cur / dur) * 100 : 0]} onValueChange={seek} max={100} step={0.1} className="flex-1" />
            <span className="font-mono text-xs text-[#A1A1AA] w-20 text-right shrink-0">{fmt(cur)} / {fmt(dur)}</span>
          </div>
        </div>
      )}
    </>
  );
}

export const BeforeAfter = ({ item }) => {
  const hasTracks = Array.isArray(item.tracks) && item.tracks.length > 0;

  return (
    <div data-testid={`before-after-${item.id}`} className="rounded-lg border border-white/10 bg-[#12141A] overflow-hidden transition-all hover:border-white/20">
      {/* header */}
      <div className="flex gap-4 p-5 md:p-6">
        {item.cover_url ? (
          <img src={item.cover_url} alt={item.title} className="h-24 w-24 md:h-28 md:w-28 rounded-md object-cover shrink-0" />
        ) : (
          <div className="h-24 w-24 md:h-28 md:w-28 rounded-md bg-[#0B0C10] border border-white/10 flex items-center justify-center shrink-0">
            <Disc3 size={32} className="text-[#C8A97E]" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h4 className="font-head text-xl font-medium leading-tight">{item.title}</h4>
          <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-3">{item.description}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            {item.project_type && <span className="text-[10px] font-mono uppercase tracking-wider text-[#C8A97E] bg-[#C8A97E]/10 border border-[#C8A97E]/20 px-2 py-0.5 rounded-sm">{item.project_type}</span>}
            {item.client && <span className="text-[11px] text-[#8b8b93]">Client: {item.client}</span>}
            {hasTracks && <span className="text-[11px] text-[#8b8b93] font-mono">{item.tracks.length} tracks</span>}
          </div>
          {item.video_url && (
            <a href={item.video_url} target="_blank" rel="noopener noreferrer" data-testid={`ba-film-${item.id}`}
              className="inline-flex items-center gap-2 mt-3 text-sm text-[#C8A97E] hover:text-[#B3956D] border border-[#C8A97E]/30 hover:border-[#C8A97E]/60 rounded-sm px-3 py-1.5 transition-colors">
              <Film size={15} /> Watch the film
            </a>
          )}
        </div>
      </div>

      {/* body */}
      {hasTracks ? (
        <div className="px-3 md:px-4 pb-4 divide-y divide-white/5 border-t border-white/5">
          {item.tracks.map((t, i) => (
            <div key={t.id || t.title || i}>
              <div className="pt-3 px-3 flex items-center justify-between">
                <span className="font-medium text-sm truncate">{t.title}</span>
              </div>
              <ABUnit before={t.before_path} after={t.after_path} index={i + 1} testid={`ba-${item.id}-track-${i}`} layout="row" />
            </div>
          ))}
        </div>
      ) : (
        <div className="px-5 md:px-6 pb-5 md:pb-6 border-t border-white/5 pt-5">
          <ABUnit before={item.before_path} after={item.after_path} testid={`ba-${item.id}`} layout="full" />
        </div>
      )}
    </div>
  );
};
